import React, { useState } from "react";
import { usePermission } from "react-permission-role"; // Import usePermission
import { useDispatch } from "react-redux";
import { Link, useNavigate } from "react-router-dom";

import { decodeJWT, setCredentials } from "features/auth/authSlice";
import { changeLanguage } from "features/language/languageSlice";
import AuthCardLayout from "layouts/AuthCardLayout";
import { clearApiCaches } from "store/store";
import { notify } from "utils/notify";

import LoginForm from "components/authentication/LoginForm";
import { useLoginUserMutation } from "components/services/authorization/authService";
import { setAccessToken } from "components/utilities/sedra3/auth";

import paths from "routes/paths";

const Login = () => {
  const [loginUser] = useLoginUserMutation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { setUser } = usePermission(); // Destructure setUser from usePermission

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleLogin = async (email, password) => {
    if (isSubmitting) return;
    setIsSubmitting(true);

    try {
      // Nothing loaded for an earlier session may be shown to this one.
      dispatch(clearApiCaches());
      // -- // -- // -- // -- // -- // -- // -- // -- //
      const response = await loginUser({ email, password }).unwrap();
      // The access token in the answer, kept in memory; the refresh token in
      // the HttpOnly cookie the answer set, out of the page's reach (#112).
      const { access_token, user } = response.data;

      // Decode token to extract roles and permissions
      const decodedToken = decodeJWT(access_token);
      const roles = decodedToken?.roles || [];
      const permissions = decodedToken?.permissions || [];

      // Dispatch user info to Redux store
      dispatch(
        setCredentials({
          user: user,
          accessToken: access_token,
        })
      );
      setAccessToken(access_token);
      localStorage.setItem("user", JSON.stringify(user));
      // Set default language to English on login
      dispatch(changeLanguage("en"));

      // Set user roles and permissions for react-permission-role
      setUser({
        id: user.id,
        roles: roles,
        permissions: permissions,
      });

      // Redirect to the dashboardRoot path after login
      navigate(paths.mainDashboard);

      notify.success("UI_LOGIN_SUCCESS_WITH_EMAIL", { email });
    } catch (error) {
      notify.apiError(error, "UI_LOGIN_FAILED");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <AuthCardLayout
      leftSideContent={
        <>
          <p className="text-white">
            Don&apos;t have an account?
            <br />
            <Link
              className="text-white text-decoration-underline"
              to={paths.cardRegister}
            >
              Register
            </Link>
          </p>
          <p className="pt-3 text-white">
            Have a technical issue?
            <br />
            <Link
              className="text-white text-decoration-underline"
              to={paths.cardTechnicalIssue}
            >
              Raise a ticket
            </Link>
          </p>
        </>
      }
    >
      <h3>Account Login</h3>
      <LoginForm layout="card" hasLabel onSubmit={handleLogin} />
    </AuthCardLayout>
  );
};

export default Login;
